import { FirebaseService, ToastService } from '@shared/lib/services';
import { formatInrDisplayAmount } from '@shared/lib/utils/inrAmountInWords';
import { Input } from '@shared/ui';
import { Button } from '@shared/ui/Button';
import { Checkbox } from '@shared/ui/Checkbox';
import { Divider, Flex, Form, Upload } from 'antd';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, UploadCloud } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useCreateCostAdjustmentRequest } from '../lib/hooks/useCreateCostAdjustmentRequest';
import useGetAlterationPrerequisites from '../lib/hooks/useGetAlterationPrerequisites';
import { useGetBriefCampaignInfluner } from '../lib/hooks/useGetBriefCampaignInfluner';
import { ICampaignAlterationApprovalRequest, ICampaignAlterationRequestPayload } from '../lib/types';
import styles from './styles.module.scss';

export interface CommercialTaxSettings {
  creatorGstEnabled: boolean;
  creatorGstPercent: number;
  creatorTdsEnabled: boolean;
  creatorTdsPercent: number;
  brandGstEnabled: boolean;
  brandGstPercent: number;
  brandServiceCostEnabled: boolean;
  brandServiceCostRate: number;
}

export interface ProofAttachment {
  id: string;
  name: string;
  url?: string;
  file?: File;
}

export const DEFAULT_COMMERCIAL_TAX_SETTINGS: CommercialTaxSettings = {
  creatorGstEnabled: true,
  creatorGstPercent: 18,
  creatorTdsEnabled: true,
  creatorTdsPercent: 10,
  brandGstEnabled: true,
  brandGstPercent: 18,
  brandServiceCostEnabled: true,
  brandServiceCostRate: 0,
};

export type CommercialChangeDirection = 'increase' | 'decrease';

export const formatSignedInr = (amount: number | null | undefined): string => {
  if (amount == null) return '—';
  if (amount < 0) {
    return `-₹${Math.abs(amount).toLocaleString('en-IN')}`;
  }
  return `+₹${amount.toLocaleString('en-IN')}`;
};

export const filesToProofAttachments = (files: File[]): ProofAttachment[] => {
  return files.map((file) => ({
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name: file.name,
    file,
  }));
};

export function computeCommercialChangePreview({
  baseInfluencer,
  baseBrand,
  influencerDelta,
  brandDelta,
  influencerDirection,
  brandDirection,
  tax,
}: {
  baseInfluencer: number | null;
  baseBrand: number | null;
  influencerDelta: number;
  brandDelta: number;
  influencerDirection: 'add' | 'subtract';
  brandDirection: 'add' | 'subtract';
  tax: CommercialTaxSettings;
}) {
  const signedInfluencerDelta = influencerDirection === 'add' ? influencerDelta : -influencerDelta;
  const signedBrandDelta = brandDirection === 'add' ? brandDelta : -brandDelta;
  const newInfluencer = baseInfluencer !== null ? baseInfluencer + signedInfluencerDelta : null;
  const newBrand = baseBrand !== null ? baseBrand + signedBrandDelta : null;

  const creatorGstAmount = tax.creatorGstEnabled ? signedInfluencerDelta * (tax.creatorGstPercent / 100) : 0;
  const creatorTdsAmount = tax.creatorTdsEnabled ? signedInfluencerDelta * (tax.creatorTdsPercent / 100) : 0;
  const brandGstAmount = tax.brandGstEnabled ? signedBrandDelta * (tax.brandGstPercent / 100) : 0;

  const influencerLedgerDelta = signedInfluencerDelta + creatorGstAmount - creatorTdsAmount;
  const brandLedgerDelta = signedBrandDelta + brandGstAmount;

  return {
    signedInfluencerDelta,
    signedBrandDelta,
    newInfluencer,
    newBrand,
    creatorGstAmount,
    creatorTdsAmount,
    brandGstAmount,
    influencerLedgerDelta,
    brandLedgerDelta,
  };
}

export const getBrandBreakdown = (total: number, serviceRate: number, gstRate: number) => {
  const divisor = (1 + serviceRate / 100) * (1 + gstRate / 100);
  if (!divisor) return { base: 0, serviceCost: 0, gst: 0 };
  const base = total / divisor;
  const serviceCost = base * (serviceRate / 100);
  const gst = (base + serviceCost) * (gstRate / 100);
  return {
    base: Math.round(base),
    serviceCost: Math.round(serviceCost),
    gst: Math.round(gst),
  };
};

interface ChangeCommercialsFormProps {
  creatorName: string;
  campaignInfluencerId: string;
  briefId?: string;
  onSuccess?: () => void;
  onCancel: () => void;
  viewOnly?: boolean;
  alterationRequest?: ICampaignAlterationApprovalRequest;
}

export const ChangeCommercialsForm: React.FC<ChangeCommercialsFormProps> = ({
  creatorName,
  campaignInfluencerId,
  briefId,
  onSuccess,
  onCancel,
  viewOnly = false,
  alterationRequest,
}) => {
  const [influencerDirection, setInfluencerDirection] = useState<'add' | 'subtract'>(
    alterationRequest ? (alterationRequest.influencerCostDirection || 'add') : 'add'
  );
  const [brandDirection, setBrandDirection] = useState<'add' | 'subtract'>(
    alterationRequest ? (alterationRequest.brandCostDirection || 'add') : 'add'
  );
  const [influencerDelta, setInfluencerDelta] = useState(
    alterationRequest ? String(recordInfluencerCost(alterationRequest)) : ''
  );
  const [brandDelta, setBrandDelta] = useState(
    alterationRequest ? String(recordBrandCost(alterationRequest)) : ''
  );
  const [remarks, setRemarks] = useState(
    alterationRequest ? alterationRequest.remarks : ''
  );
  const [tax, setTax] = useState<CommercialTaxSettings>({ ...DEFAULT_COMMERCIAL_TAX_SETTINGS });
  const [showAdvancedTax, setShowAdvancedTax] = useState(!!alterationRequest);
  const [proofAttachments, setProofAttachments] = useState<ProofAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [proofUploadUrl, setProofUploadUrl] = useState<string>("");

  const isCompletedStatus =
    alterationRequest?.status === "Approved" || alterationRequest?.status === "Rejected";

  const { campaignInfluencer, isFetching: isLoadingInf } = useGetBriefCampaignInfluner({
    campaignInfluencerId,
    enabled: !!campaignInfluencerId && !isCompletedStatus,
  });

  const initialPrerequisitePayload: ICampaignAlterationRequestPayload = useMemo(
    () => ({
      campaignInfluencerId,
      type: "Cost-Adjustment",
      toAlterBrandCost: false,
      brandCostBaseCost: 0,
      brandCostDirection: "add",
      applyBrandCostServiceCostRate: true,
      applyBrandCostGstRate: true,
      toAlterInfluencerCost: false,
      influencerCostBaseCost: 0,
      influencerCostDirection: "add",
      applyInfluencerCostGstRate: true,
      applyInfluencerCostTdsRate: true,
      remarks: "",
    }),
    [campaignInfluencerId]
  );

  const { alterationPrerequisite: initialTaxPrerequisite } = useGetAlterationPrerequisites(
    initialPrerequisitePayload,
    !!campaignInfluencerId && !isCompletedStatus
  );

  useEffect(() => {
    const prereqData = initialTaxPrerequisite?.data?.new || initialTaxPrerequisite?.data?.old;
    if (!prereqData) return;

    setTax((prev) => ({
      ...prev,
      creatorGstPercent: prereqData.influencerCostGstRate ?? prev.creatorGstPercent,
      creatorTdsPercent: prereqData.influencerCostTdsRate ?? prev.creatorTdsPercent,
      brandGstPercent: prereqData.brandCostGstRate ?? prev.brandGstPercent,
      brandServiceCostRate: prereqData.brandCostServiceCostRate ?? prev.brandServiceCostRate,
    }));
  }, [initialTaxPrerequisite?.data]);

  const payload: ICampaignAlterationRequestPayload = {
    campaignInfluencerId: campaignInfluencerId,
    type: "Cost-Adjustment",
    toAlterBrandCost: Number(brandDelta) > 0,
    brandCostBaseCost: Number(brandDelta) || 0,
    brandCostDirection: brandDirection,
    applyBrandCostGstRate: tax.brandGstEnabled,
    applyBrandCostServiceCostRate: tax.brandServiceCostEnabled,
    toAlterInfluencerCost: Number(influencerDelta) > 0,
    influencerCostBaseCost: Number(influencerDelta) || 0,
    influencerCostDirection: influencerDirection,
    applyInfluencerCostGstRate: tax.creatorGstEnabled,
    applyInfluencerCostTdsRate: tax.creatorTdsEnabled,
    remarks: remarks.trim(),
    proofUploadUrl: proofUploadUrl || undefined,
  };

  const [debouncedPayload] = useDebounce(() => payload, 250);

  const {
    alterationPrerequisite,
    alterationPrerequisiteError,
    isAlterationPrerequisiteFetching
  } = useGetAlterationPrerequisites(debouncedPayload(), ((!alterationRequest || alterationRequest?.status === "Pending") && !isLoadingInf));

  useEffect(() => {
    if (alterationRequest && !isCompletedStatus) {
      setTax((prev) => ({
        ...prev,
        creatorGstEnabled: alterationRequest.applyInfluencerCostGstRate ?? prev.creatorGstEnabled,
        creatorTdsEnabled: alterationRequest.applyInfluencerCostTdsRate ?? prev.creatorTdsEnabled,
        brandGstEnabled: alterationRequest.applyBrandCostGstRate ?? prev.brandGstEnabled,
        brandServiceCostEnabled: alterationRequest.applyBrandCostServiceCostRate ?? prev.brandServiceCostEnabled,
      }));
    }
  }, [alterationPrerequisite, alterationRequest]);

  useEffect(() => {
    if (isCompletedStatus && alterationRequest) {
      setTax((prev) => ({
        ...prev,
        creatorGstEnabled: alterationRequest.applyInfluencerCostGstRate ?? prev.creatorGstEnabled,
        creatorGstPercent: alterationRequest?.preCheckResult?.old?.influencerCostGstRate ?? prev.creatorGstPercent,
        creatorTdsEnabled: alterationRequest.applyInfluencerCostTdsRate ?? prev.creatorTdsEnabled,
        creatorTdsPercent: alterationRequest?.preCheckResult?.old?.influencerCostTdsRate ?? prev.creatorTdsPercent,
        brandGstEnabled: alterationRequest.applyBrandCostGstRate ?? prev.brandGstEnabled,
        brandGstPercent: alterationRequest?.preCheckResult?.old?.brandCostGstRate ?? prev.brandGstPercent,
        brandServiceCostEnabled: alterationRequest.applyBrandCostServiceCostRate ?? prev.brandServiceCostEnabled,
        brandServiceCostRate: alterationRequest?.preCheckResult?.old?.brandCostServiceCostRate ?? prev.brandServiceCostRate,
      }));
    } else if (!alterationRequest) {
      setTax((prev) => ({
        ...prev,
        creatorGstEnabled: true,
        creatorTdsEnabled: true,
        brandGstEnabled: true,
        brandServiceCostEnabled: true,
      }));
    }
  }, [isCompletedStatus, alterationRequest]);

  const { createCostAdjustment, isCreating } = useCreateCostAdjustmentRequest(briefId, onSuccess);

  function recordInfluencerCost(req: ICampaignAlterationApprovalRequest) {
    return req.influencerCostBaseCost || 0;
  }

  function recordBrandCost(req: ICampaignAlterationApprovalRequest) {
    return req.brandCostBaseCost || 0;
  }

  const baseInfluencerCommercial =  (!alterationRequest || alterationRequest?.status === "Pending") ? alterationPrerequisite?.data?.old?.totalInfluencerBaseCost || 0 : alterationRequest?.preCheckResult?.old?.totalInfluencerBaseCost || 0;

  const baseBrandCommercial = (!alterationRequest || alterationRequest?.status === "Pending") ? alterationPrerequisite?.data?.old?.totalBrandSellingCost || 0 : alterationRequest?.preCheckResult?.old?.totalBrandSellingCost || 0;

  const newInfluencerCommercial = (!alterationRequest || alterationRequest?.status === "Pending") ? alterationPrerequisite?.data?.new?.totalInfluencerBaseCost || 0 : alterationRequest?.preCheckResult?.new?.totalInfluencerBaseCost || 0;

  const newBrandCommercial = (!alterationRequest || alterationRequest?.status === "Pending") ? alterationPrerequisite?.data?.new?.totalBrandSellingCost || 0 : alterationRequest?.preCheckResult?.new?.totalBrandSellingCost || 0;

  const influencerCostGstRate = (!alterationRequest || alterationRequest?.status === "Pending") ? alterationPrerequisite?.data?.new?.influencerCostGstRate || 0 : alterationRequest?.preCheckResult?.new?.influencerCostGstRate || 0;

  const brandCostGstRate = (!alterationRequest || alterationRequest?.status === "Pending") ? alterationPrerequisite?.data?.new?.brandCostGstRate || 0 : alterationRequest?.preCheckResult?.new?.brandCostGstRate || 0;

  const influencerCostTdsRate = (!alterationRequest || alterationRequest?.status === "Pending") ? alterationPrerequisite?.data?.new?.influencerCostTdsRate || 0 : alterationRequest?.preCheckResult?.new?.influencerCostTdsRate || 0;

  const oldBrandServiceCostRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.brandCostServiceCostRate || 0
    : alterationRequest?.preCheckResult?.old?.brandCostServiceCostRate || 0;

  const oldBrandGstRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.brandCostGstRate || 0
    : alterationRequest?.preCheckResult?.old?.brandCostGstRate || 0;

  const newBrandServiceCostRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.brandCostServiceCostRate || 0
    : alterationRequest?.preCheckResult?.new?.brandCostServiceCostRate || 0;

  const newBrandGstRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.new?.brandCostGstRate || 0
    : alterationRequest?.preCheckResult?.new?.brandCostGstRate || 0;

  const oldBrandBreakdown = useMemo(() => {
    return getBrandBreakdown(baseBrandCommercial, oldBrandServiceCostRate, oldBrandGstRate);
  }, [baseBrandCommercial, oldBrandServiceCostRate, oldBrandGstRate]);

  const newBrandBreakdown = useMemo(() => {
    return getBrandBreakdown(newBrandCommercial, newBrandServiceCostRate, newBrandGstRate);
  }, [newBrandCommercial, newBrandServiceCostRate, newBrandGstRate]);

  const brandBaseDelta = newBrandBreakdown.base - oldBrandBreakdown.base;
  const brandServiceCostDelta = newBrandBreakdown.serviceCost - oldBrandBreakdown.serviceCost;
  const brandGstDelta = newBrandBreakdown.gst - oldBrandBreakdown.gst;
  const brandNetChange = newBrandCommercial - baseBrandCommercial;

  const oldInfluencerCostGstRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.influencerCostGstRate || 0
    : alterationRequest?.preCheckResult?.old?.influencerCostGstRate || 0;

  const oldInfluencerCostTdsRate = (!alterationRequest || alterationRequest?.status === "Pending")
    ? alterationPrerequisite?.data?.old?.influencerCostTdsRate || 0
    : alterationRequest?.preCheckResult?.old?.influencerCostTdsRate || 0;

  const oldInfluencerBreakdown = useMemo(() => {
    const gst = baseInfluencerCommercial * (oldInfluencerCostGstRate / 100);
    const tds = baseInfluencerCommercial * (oldInfluencerCostTdsRate / 100);
    const net = baseInfluencerCommercial + gst - tds;
    return { gst: Math.round(gst), tds: Math.round(tds), net: Math.round(net) };
  }, [baseInfluencerCommercial, oldInfluencerCostGstRate, oldInfluencerCostTdsRate]);

  const newInfluencerBreakdown = useMemo(() => {
    const gst = newInfluencerCommercial * (influencerCostGstRate / 100);
    const tds = newInfluencerCommercial * (influencerCostTdsRate / 100);
    const net = newInfluencerCommercial + gst - tds;
    return { gst: Math.round(gst), tds: Math.round(tds), net: Math.round(net) };
  }, [newInfluencerCommercial, influencerCostGstRate, influencerCostTdsRate]);

  const influencerBaseDelta = newInfluencerCommercial - baseInfluencerCommercial;
  const influencerGstDelta = newInfluencerBreakdown.gst - oldInfluencerBreakdown.gst;
  const influencerTdsDelta = newInfluencerBreakdown.tds - oldInfluencerBreakdown.tds;
  const influencerNetChange = newInfluencerBreakdown.net - oldInfluencerBreakdown.net;

  const commercialPreview = useMemo(
    () =>
      computeCommercialChangePreview({
        baseInfluencer: baseInfluencerCommercial,
        baseBrand: baseBrandCommercial,
        influencerDelta: Math.abs(baseInfluencerCommercial - newInfluencerCommercial),
        brandDelta: Math.abs(baseBrandCommercial - newBrandCommercial),
        influencerDirection,
        brandDirection,
        tax,
      }),
    [alterationPrerequisite, alterationRequest, influencerDelta, brandDelta, influencerDirection, brandDirection, tax]
  );

  const canSubmit =
    (Number(influencerDelta) > 0 || Number(brandDelta) > 0) &&
    remarks.trim().length > 0 &&
    proofAttachments.length > 0;

  const handleBeforeUpload = async (file: File) => {
    setProofAttachments((prev) => [...filesToProofAttachments([file])]);
    try {
      setIsUploading(true);
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        'alteration-proofs',
        file
      );
      setProofUploadUrl(uploadedUrl);
    } catch (error) {
      ToastService.error('Failed to upload proof file. Please try again.');
    } finally {
      setIsUploading(false);
    }
    return false; // Prevent antd default upload
  };

  const removeProofFile = (id: string) => {
    setProofAttachments((prev) => {
      const updated = prev.filter((f) => f.id !== id);
      if (updated.length === 0) {
        setProofUploadUrl('');
      }
      return updated;
    });
  };

  const handleConfirm = () => {
    if (!canSubmit) return;
    createCostAdjustment({ ...payload, proofUploadUrl });
  };

  return (
    <Form layout="vertical" onFinish={handleConfirm} className={styles.formContainer}>
      {/* Current Commercials */}
      <div className={styles.currentCommercialsCard}>
        <p className={styles.cardLabel}>Commercials</p>
        <div className={styles.grid}>
          <div>
            <p className={styles.valueLabel}>Current Influencer Commercial</p>
            <p className={styles.valueAmount}>
              {baseInfluencerCommercial != null ? formatInrDisplayAmount(baseInfluencerCommercial) : '—'}
            </p>
            <Form.Item label={<span className={styles.label}>Influencer commercial change</span>} style={{ marginBottom: 0 }}>
          <div className={styles.inputWithDirection}>
            <div className={styles.arrowButtons}>
              <Button
                className={`${styles.arrowBtn} ${influencerDirection === 'add' ? styles.activeUp : ''}`}
                onClick={viewOnly ? undefined : () => setInfluencerDirection('add')}
                disabled={viewOnly}
                icon={<ArrowUp size={16} />}
                size='small'
              />
                
              <Button
                className={`${styles.arrowBtn} ${influencerDirection === 'subtract' ? styles.activeDown : ''}`}
                onClick={viewOnly ? undefined : () => setInfluencerDirection('subtract')}
                disabled={viewOnly}
                icon={<ArrowDown size={16} />}
                size='small'
              />
            </div>
            <div className={styles.inputWrapper}>
              <Input
                type="number"
                min={0}
                prefix="₹"
                value={influencerDelta}
                onChange={(e) => setInfluencerDelta(e.target.value)}
                placeholder="0"
                disabled={viewOnly}
              />
            </div>
          </div>
          {baseInfluencerCommercial != null && Number(influencerDelta) > 0 && (
            <div style={{ marginTop: 4, fontSize: '12px', color: '#697386' }}>
              {`₹${baseInfluencerCommercial.toLocaleString('en-IN')} ${influencerDirection === 'add' ? '+' : '-'} ₹${Number(influencerDelta).toLocaleString('en-IN')} = `}
              <span style={{ color: influencerDirection === 'add' ? '#16a34a' : '#e11d48', fontWeight: 600 }}>
                ₹{(influencerDirection === 'add' ? baseInfluencerCommercial + Number(influencerDelta) : baseInfluencerCommercial - Number(influencerDelta)).toLocaleString('en-IN')}
              </span>
            </div>
          )}
        </Form.Item>
          </div>
          <div>
            <p className={styles.valueLabel}>Current Brand Commercial</p>
            <p className={styles.valueAmount}>
              {baseBrandCommercial != null ? formatInrDisplayAmount(baseBrandCommercial) : '—'}
            </p>
            <Form.Item label={<span className={styles.label}>Brand commercial change</span>} style={{ marginBottom: 0 }}>
          <div className={styles.inputWithDirection}>
            <div className={styles.arrowButtons}>
              <Button
                className={`${styles.arrowBtn} ${brandDirection === 'add' ? styles.activeUp : ''}`}
                onClick={viewOnly ? undefined : () => setBrandDirection('add')}
                disabled={viewOnly}
                icon={<ArrowUp size={16} />}
                size='small'
              />
                
              <Button
                className={`${styles.arrowBtn} ${brandDirection === 'subtract' ? styles.activeDown : ''}`}
                onClick={viewOnly ? undefined : () => setBrandDirection('subtract')}
                disabled={viewOnly}
                icon={<ArrowDown size={16} />}
                size='small'
              />
                             </div>
            <div className={styles.inputWrapper}>
              <Input
                type="number"
                min={0}
                prefix="₹"
                value={brandDelta}
                onChange={(e) => setBrandDelta(e.target.value)}
                placeholder="0"
                disabled={viewOnly}
              />
            </div>
          </div>
          {baseBrandCommercial != null && Number(brandDelta) > 0 && (
            <div style={{ marginTop: 4, fontSize: '12px', color: '#697386' }}>
              {`₹${baseBrandCommercial.toLocaleString('en-IN')} ${brandDirection === 'add' ? '+' : '-'} ₹${Number(brandDelta).toLocaleString('en-IN')} = `}
              <span style={{ color: brandDirection === 'add' ? '#16a34a' : '#e11d48', fontWeight: 600 }}>
                ₹{(brandDirection === 'add' ? baseBrandCommercial + Number(brandDelta) : baseBrandCommercial - Number(brandDelta)).toLocaleString('en-IN')}
              </span>
            </div>
          )}
        </Form.Item>
          </div>
        </div>
        {/* Advanced Tax Accordion */}
      <div style={{marginTop: 10}}>
        <button
          type="button"
          className={styles.advancedTaxToggle}
          onClick={() => setShowAdvancedTax(!showAdvancedTax)}
        >
          {showAdvancedTax ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          Advanced tax settings
        </button>
        {showAdvancedTax && (
          <div className={styles.advancedTaxContainer} style={{ padding: '12px' }}>
            <Flex gap={16} align="stretch" style={{ width: '100%' }}>
              {/* Creator Tax Section */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span className={styles.label} style={{ fontSize: '11px', fontWeight: 700, color: '#697386', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Creator
                </span>

                <div className={styles.taxRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Checkbox
                    checked={tax.creatorGstEnabled}
                    onChange={(e) =>
                      setTax((prev) => ({
                        ...prev,
                        creatorGstEnabled: e.target.checked,
                      }))
                    }
                    disabled={viewOnly}
                  >
                    <span style={{ fontSize: '13px', color: '#1a1f36', marginLeft: 4 }}>GST on creator commercial</span>
                  </Checkbox>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: '#1a1f36' }}>{`${tax.creatorGstPercent ?? 18}%`}</span>
                </div>

                <div className={styles.taxRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Checkbox
                    checked={tax.creatorTdsEnabled}
                    onChange={(e) =>
                      setTax((prev) => ({
                        ...prev,
                        creatorTdsEnabled: e.target.checked,
                      }))
                    }
                    disabled={viewOnly}
                  >
                    <span style={{ fontSize: '13px', color: '#1a1f36', marginLeft: 4 }}>TDS on creator commercial</span>
                  </Checkbox>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: '#1a1f36' }}>{`${tax.creatorTdsPercent ?? influencerCostTdsRate ?? 10}%`}</span>
                </div>
              </div>

              {/* Vertical Divider */}
              <Divider type="vertical" style={{ height: 'auto', margin: 0, borderColor: '#e3e8ee' }} />

              {/* Brand Tax Section */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span className={styles.label} style={{ fontSize: '11px', fontWeight: 700, color: '#697386', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Brand
                </span>

                <div className={styles.taxRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Checkbox
                    checked={tax.brandGstEnabled}
                    onChange={(e) =>
                      setTax((prev) => ({
                        ...prev,
                        brandGstEnabled: e.target.checked,
                      }))
                    }
                    disabled={viewOnly}
                  >
                    <span style={{ fontSize: '13px', color: '#1a1f36', marginLeft: 4 }}>GST on brand commercial</span>
                  </Checkbox>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: '#1a1f36' }}>{`${tax.brandGstPercent ?? brandCostGstRate ?? 18}%`}</span>
                </div>

                <div className={styles.taxRow} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Checkbox
                    checked={tax.brandServiceCostEnabled}
                    onChange={(e) =>
                      setTax((prev) => ({
                        ...prev,
                        brandServiceCostEnabled: e.target.checked,
                      }))
                    }
                    disabled={viewOnly}
                  >
                    <span style={{ fontSize: '13px', color: '#1a1f36', marginLeft: 4 }}>Brand Service Cost</span>
                  </Checkbox>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: '#1a1f36' }}>{`${tax.brandServiceCostRate ?? 0}%`}</span>
                </div>
              </div>
            </Flex>
          </div>
        )}
      </div>
      </div>

      {/* Remarks */}
      <Form.Item label={<span className={styles.label}>Remarks</span>} style={{ marginBottom: 0 }}>
        <Input.TextArea
          className={styles.textarea}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Reason for this change..."
          rows={3}
          disabled={viewOnly}
        />
      </Form.Item>

      {/* Proof of purchase */}
        {!viewOnly && <Form.Item
          label={<span className={styles.label}>Proof of Purchase</span>}
          required
          style={{ marginBottom: 0 }}
        >
          <Upload.Dragger
            name="file"
            multiple={false}
            beforeUpload={handleBeforeUpload}
            fileList={proofAttachments.length ? [{uid: proofAttachments[0].id, name: proofAttachments[0].name, url: proofUploadUrl}] : []}
            onRemove={(file) => {
              removeProofFile(file.uid);
            }}
            showUploadList={true}
            accept=".pdf,.png,.jpg,.jpeg"
            maxCount={1}
            disabled={!!viewOnly}
          >
            <div className={styles.uploadPrompt}>
              <UploadCloud size={24} className={styles.icon} />
              <p className={styles.promptTitle}>
                <span>Click to upload</span> or drag and drop
              </p>
              <p className={styles.promptSub}>PDF, PNG, JPG up to 10MB</p>
            </div>
          </Upload.Dragger>
        </Form.Item>}

        {viewOnly && 
        <Button type='link' size='small' onClick={() => {
          window.open(alterationRequest?.proofUploadUrl, "_blank");
        }} className={styles.advancedTaxToggle} style={{width: "fit-content"}}>Proof of Alteration</Button>}

      {/* Preview Summary */}
      {/* <div className={styles.previewCard}>
        <p className={styles.previewTitle}>Preview</p>
        {isAlterationPrerequisiteFetching ? (
          <Skeleton active />
        ) : (
          <div className={styles.previewContainer}>
            <div className={styles.previewColumn}>
              <h4 className={styles.columnTitle}>Influencer Ledger</h4>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>New commercials</span>
                <span className={styles.previewValue}>
                  {`${formatSignedInr(influencerBaseDelta)}`}
                </span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>GST</span>
                <span className={styles.previewValue}>{formatSignedInr(influencerGstDelta)}</span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>TDS</span>
                <span className={styles.previewValue}>{formatSignedInr(-influencerTdsDelta)}</span>
              </div>
              <Divider className={styles.previewDivider} />
              <div className={styles.previewRowTotal}>
                <span className={styles.previewLabelTotal}>Total net change</span>
                <span className={styles.previewValueTotal}>{formatSignedInr(influencerNetChange)}</span>
              </div>
            </div>

            <div className={styles.verticalDivider} />

            <div className={styles.previewColumn}>
              <h4 className={styles.columnTitle}>Brand Ledger</h4>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>New Commercials</span>
                <span className={styles.previewValue}>
                  {`${formatSignedInr(brandBaseDelta)}`}
                </span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>GST</span>
                <span className={styles.previewValue}>{formatSignedInr(brandGstDelta)}</span>
              </div>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>Service cost</span>
                <span className={styles.previewValue}>{formatSignedInr(brandServiceCostDelta)}</span>
              </div>
              <Divider className={styles.previewDivider} />
              <div className={styles.previewRowTotal}>
                <span className={styles.previewLabelTotal}>Total net change</span>
                <span className={styles.previewValueTotal}>{formatSignedInr(brandNetChange)}</span>
              </div>
            </div>
          </div>
        )}
      </div> */}

      {!viewOnly && (
        <div className={styles.footer}>
          <button type="button" onClick={onCancel} className={styles.cancelBtn}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || isCreating || isUploading}
            className={styles.confirmBtn}
          >
            Apply Changes
          </button>
        </div>
      )}
    </Form>
  );
};
